import React from "react";
import { ArrowRight } from "lucide-react";
import artieImage from "../../assets/images/artie.png";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useParams, useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { ArtieScoreData, Recommendation, TacticRecommendation } from "@/types/artie-score";

interface ScoreStatusResponse {
  score_data: ArtieScoreData;
}

const TaskPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  const { data: scoreStatus, isLoading, error } = useQuery<ScoreStatusResponse>({
    queryKey: ['artieScoreStatus', projectId],
    queryFn: () => {
      if (!projectId) {
        throw new Error("Project ID is missing");
      }
      return apiClient.getArtieScoreStatus(projectId) as Promise<ScoreStatusResponse>;
    },
    enabled: !!projectId,
  });

  // Use new V2 tactics recommendations, fallback to legacy if not available
  const currentRecommendations = scoreStatus?.score_data?.current_recommendations?.slice(0, 3);
  const legacyRecommendations = scoreStatus?.score_data?.recommendations?.slice(0, 3);
  const recommendations = currentRecommendations?.length ? currentRecommendations : legacyRecommendations;

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">
        <div className="w-full max-w-5xl px-6 py-12 flex flex-col items-center flex-grow">
          <div className="flex flex-col items-center text-center max-w-3xl mb-12 relative w-full">
            <div className="mb-8 relative flex justify-center w-full">
              <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50 left-1/2 -translate-x-1/2 w-48 h-48"></div>
              <img
                src={artieImage}
                alt="Artie"
                className="w-48 h-48 object-contain relative z-10"
              />
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              What we need before we take this to brands.
            </h1>

            <div className="space-y-1 text-[oklch(0.55_0_0)] text-lg leading-relaxed font-ibm max-w-2xl">
              <p>
                These top 3 tasks help get your project into a strong position
                for the marketplace. You can take them on now, or assign them to
                someone you trust.
              </p>
            </div>
          </div>

          <div className="w-full max-w-3xl bg-gray-50 rounded-[2rem] p-8 md:p-12 mb-8 border border-gray-100 shadow-sm relative overflow-visible">
            {isLoading && <Skeleton className="h-96" />}
            {error && <p className="text-red-500">Error: {error.message}</p>}

            {!isLoading && !error && recommendations && (
              <>
                <div className="space-y-8">
                  {recommendations.map((task: Recommendation | TacticRecommendation, index: number) => {
                    // Check if this is a V2 tactic recommendation or legacy recommendation
                    const isV2Tactic = 'tactic_id' in task;
                    
                    return (
                      <div key={task.id || `task-${index}`} className="flex gap-6 items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-black rounded-full flex items-center justify-center text-white font-bold text-sm mt-1">
                          {index + 1}
                        </div>
                        <div className="flex-grow space-y-1">
                          <h3 className="text-lg font-bold text-black leading-tight">
                            {isV2Tactic ? (task as TacticRecommendation).tactic_name : (task as Recommendation).what_to_improve}
                          </h3>
                          <p className="text-gray-500 text-base leading-relaxed">
                            {isV2Tactic ? (task as TacticRecommendation).gap_description : (task as Recommendation).why_it_matters}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-end sm:items-center mt-12 pt-6 border-t border-transparent">
                  <div className="text-sm text-gray-500 mb-6 sm:mb-0">
                    <span className="block text-gray-700 font-bold mb-0.5">
                      First matching window closes soon
                    </span>
                    Complete your tasks by then to be eligible for our first
                    round <br />  of brand matching.
                  </div>
                  <Button
                    className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all text-base w-full sm:w-auto"
                    onClick={() => navigate(`/project-v2/task-detail-page/${projectId}`)}
                    disabled={isLoading}
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </div>

          <div className="flex flex-col items-center gap-6 mt-4 relative w-full max-w-2xl">
            <div className="text-center">
              <p className="text-[oklch(0.55_0_0)] font-medium mb-1">
                Not feeling ready yet?
              </p>
              <p className="text-[oklch(0.55_0_0)] text-sm">
                You can skip this for now and keep exploring Artie without
                committing.
              </p>
            </div>

            <Button
              variant="link"
              className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] transition-colors"
              onClick={() => navigate(`/project-v2/project-details-v2/${projectId}`)}
            >
              Skip <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TaskPage;
